import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMe } from '../hooks/useMe';
import * as settings from '../api/settings';
import type { SettingsUser } from '../api/settings';
import { InviteUserModal } from './InviteUserModal';
import { EditUserModal } from './EditUserModal';
import { SettingsToast } from './SettingsToast';

/**
 * Users management tab. Lists every user (super-admin only), filterable by
 * "include disabled". Each row exposes Edit + Disable/Re-enable.
 *
 * `listUsers` now returns `companies` + `personalSharedWith` per user, so we
 * render both as pills inline. `companyLookup` resolves teamId → displayName
 * + color via a pre-fetched `listCompanies` call (falls back to raw teamId
 * when unseen — shouldn't happen for super-admin).
 */
export function UsersTab() {
  const { me } = useMe();
  const [users, setUsers] = useState<SettingsUser[]>([]);
  const [companies, setCompanies] = useState<settings.SettingsCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<SettingsUser | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [list, companyList] = await Promise.all([
        settings.listUsers(includeDisabled),
        settings.listCompanies(),
      ]);
      setUsers(list);
      setCompanies(companyList);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [includeDisabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Canonical company lookup from the super-admin view of ALL companies,
  // not just ones the caller is in.
  const companyLookup = useMemo(() => {
    const map = new Map<string, { displayName: string; color: string }>();
    companies.forEach((c) =>
      map.set(c.teamId, { displayName: c.displayName, color: c.color })
    );
    return map;
  }, [companies]);

  const userLookup = useMemo(() => {
    const map = new Map<string, { displayName: string; color: string }>();
    users.forEach((u) =>
      map.set(u.userId, { displayName: u.displayName, color: u.color })
    );
    return map;
  }, [users]);

  const onDisable = useCallback(
    async (u: SettingsUser) => {
      if (!window.confirm(`Disable ${u.displayName}? They will lose access.`)) return;
      try {
        await settings.disableUser(u.userId);
        setToast(`${u.displayName} disabled`);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      }
    },
    [refresh]
  );

  const onReenable = useCallback(
    async (u: SettingsUser) => {
      try {
        await settings.updateUser(u.userId, { disabled: false });
        setToast(`${u.displayName} re-enabled`);
        await refresh();
      } catch (err) {
        alert((err as Error).message);
      }
    },
    [refresh]
  );

  const sorted = useMemo(
    () => [...users].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [users]
  );

  const nonDisabledUsers = useMemo(
    () => users.filter((u) => !u.disabled),
    [users]
  );

  // NOTE: We don't know which companies each user is a member of without
  // additional API calls. As a best-effort, we display no company pills on
  // rows — only the super-admin's own companies show up elsewhere. If this
  // becomes an operational need, add a per-user membership endpoint to the
  // backend (see ADR 0009 §4 API-surface gap).

  return (
    <div className="settings-tab-body">
      <div className="settings-tab-toolbar">
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => setIncludeDisabled(e.target.checked)}
          />
          <span>Include disabled</span>
        </label>
        <button
          type="button"
          className="primary"
          onClick={() => setInviteOpen(true)}
        >
          + Invite user
        </button>
      </div>

      {loading ? (
        <div className="settings-loading muted">Loading users…</div>
      ) : error ? (
        <div className="settings-error">{error}</div>
      ) : sorted.length === 0 ? (
        <div className="settings-empty muted">
          {includeDisabled ? 'No users yet.' : 'No active users.'}
        </div>
      ) : (
        <>
          <div className="settings-users-table" role="table">
            <div className="settings-users-head" role="row">
              <div role="columnheader">Name</div>
              <div role="columnheader">Handles</div>
              <div role="columnheader">Companies</div>
              <div role="columnheader">Personal shared with</div>
              <div role="columnheader" className="settings-users-actions-cell">Actions</div>
            </div>
            {sorted.map((u) => {
              const disabled = u.disabled === true;
              return (
                <div
                  key={u.userId}
                  role="row"
                  className={`settings-users-row ${disabled ? 'is-disabled' : ''}`}
                >
                  <div role="cell" className="settings-user-name">
                    <span
                      className="settings-color-dot"
                      style={{ backgroundColor: u.color }}
                      aria-hidden="true"
                    />
                    <span>{u.displayName}</span>
                    {disabled && <span className="settings-chip settings-chip-muted">disabled</span>}
                  </div>
                  <div role="cell" className="settings-user-handles">
                    {u.handles.length === 0
                      ? <span className="muted">—</span>
                      : u.handles.join(', ')}
                  </div>
                  <div role="cell" className="settings-user-companies">
                    {u.companies.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      u.companies.map((id) => {
                        const c = companyLookup.get(id);
                        return (
                          <span
                            key={id}
                            className="settings-chip"
                            style={{ background: c ? `${c.color}22` : undefined, color: c?.color }}
                          >
                            {c?.displayName ?? id}
                          </span>
                        );
                      })
                    )}
                  </div>
                  <div role="cell" className="settings-user-shares">
                    {u.personalSharedWith.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      u.personalSharedWith.map((sub) => {
                        const p = userLookup.get(sub);
                        return (
                          <span key={sub} className="settings-chip">
                            {p?.displayName ?? sub.slice(0, 8) + '…'}
                          </span>
                        );
                      })
                    )}
                  </div>
                  <div role="cell" className="settings-user-actions">
                    <button type="button" onClick={() => setEditing(u)}>
                      Edit
                    </button>
                    {disabled ? (
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => void onReenable(u)}
                      >
                        Re-enable
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="danger"
                        disabled={me?.sub === u.userId}
                        title={me?.sub === u.userId ? 'You cannot disable yourself' : undefined}
                        onClick={() => void onDisable(u)}
                      >
                        Disable
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile card list — same data, stacked layout */}
          <div className="settings-users-cards">
            {sorted.map((u) => {
              const disabled = u.disabled === true;
              return (
                <div
                  key={u.userId}
                  className={`settings-user-card ${disabled ? 'is-disabled' : ''}`}
                >
                  <div className="settings-user-card-head">
                    <span
                      className="settings-color-dot"
                      style={{ backgroundColor: u.color }}
                      aria-hidden="true"
                    />
                    <strong>{u.displayName}</strong>
                    {disabled && <span className="settings-chip settings-chip-muted">disabled</span>}
                  </div>
                  {u.handles.length > 0 && (
                    <div className="settings-user-card-line">
                      <span className="settings-user-card-key">Handles</span>
                      <span>{u.handles.join(', ')}</span>
                    </div>
                  )}
                  {u.knownAddresses.length > 0 && (
                    <div className="settings-user-card-line">
                      <span className="settings-user-card-key">Addresses</span>
                      <span>{u.knownAddresses.join(', ')}</span>
                    </div>
                  )}
                  {u.companies.length > 0 && (
                    <div className="settings-user-card-line">
                      <span className="settings-user-card-key">Companies</span>
                      <span>
                        {u.companies.map((id) => {
                          const c = companyLookup.get(id);
                          return (
                            <span
                              key={id}
                              className="settings-chip"
                              style={{ background: c ? `${c.color}22` : undefined, color: c?.color }}
                            >
                              {c?.displayName ?? id}
                            </span>
                          );
                        })}
                      </span>
                    </div>
                  )}
                  {u.personalSharedWith.length > 0 && (
                    <div className="settings-user-card-line">
                      <span className="settings-user-card-key">Personal shared with</span>
                      <span>
                        {u.personalSharedWith.map((sub) => {
                          const p = userLookup.get(sub);
                          return (
                            <span key={sub} className="settings-chip">
                              {p?.displayName ?? sub.slice(0, 8) + '…'}
                            </span>
                          );
                        })}
                      </span>
                    </div>
                  )}
                  <div className="settings-user-card-actions">
                    <button type="button" onClick={() => setEditing(u)}>
                      Edit
                    </button>
                    {disabled ? (
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => void onReenable(u)}
                      >
                        Re-enable
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="danger"
                        disabled={me?.sub === u.userId}
                        onClick={() => void onDisable(u)}
                      >
                        Disable
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <InviteUserModal
        open={inviteOpen}
        users={nonDisabledUsers}
        companies={[...companyLookup.entries()].map(([id, v]) => ({ id, ...v }))}
        onClose={() => setInviteOpen(false)}
        onDone={async (msg) => {
          setInviteOpen(false);
          setToast(msg);
          await refresh();
        }}
      />
      <EditUserModal
        user={editing}
        users={nonDisabledUsers}
        companies={[...companyLookup.entries()].map(([id, v]) => ({ id, ...v }))}
        onClose={() => setEditing(null)}
        onDone={async (msg) => {
          setEditing(null);
          setToast(msg);
          await refresh();
        }}
      />
      <SettingsToast message={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
